import React, { useEffect, useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import { IoSearchSharp } from "react-icons/io5";
import { escapeRegExp } from "@mui/x-data-grid/internals";
import { RxCross2 } from "react-icons/rx";
import { Tooltip } from "@mui/material";
import { TbTrash } from "react-icons/tb";
import { FiEdit, FiEye } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import { HiAdjustmentsHorizontal } from "react-icons/hi2";
import { MdRateReview } from "react-icons/md";
import { FaFileExport } from "react-icons/fa";
import CsvHandler from "./CsvHandler";
import { ROLES } from "../uitls/Utils";

// Utility function to recursively search within nested objects and arrays
const searchInNestedObject = (obj, searchRegex) => {
  // Handle null/undefined cases
  if (obj === null || obj === undefined) return false;

  // Handle primitive values
  if (typeof obj !== "object") {
    return searchRegex.test(String(obj));
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.some((item) => searchInNestedObject(item, searchRegex));
  }

  // Handle objects - search in all values recursively
  try {
    return Object.values(obj).some((value) => {
      return searchInNestedObject(value, searchRegex);
    });
  } catch (error) {
    // Handle specific errors that might occur with Object.values()
    if (
      error instanceof TypeError &&
      error.message.includes("Cannot convert")
    ) {
      // Object.values() failed, try to convert to string and search
      return searchRegex.test(String(obj));
    }
    // Re-throw unexpected errors
    throw error;
  }
};

// Utility function to check if a single field value matches the search criteria
const doesFieldMatchSearch = (fieldValue, searchRegex) => {
  // Early return for null/undefined
  if (fieldValue === undefined || fieldValue === null) {
    return false;
  }

  // Handle primitive types directly
  if (typeof fieldValue !== "object") {
    return searchRegex.test(String(fieldValue));
  }

  // For objects, use the nested search function
  return searchInNestedObject(fieldValue, searchRegex);
};

// Main utility function to filter an array of rows based on search string
const filterRowsBySearch = (rowData, searchString) => {
  // Return all data if no search string
  if (!searchString || searchString.trim() === "") {
    return Array.isArray(rowData) ? rowData : [];
  }

  // Validate input data
  if (!Array.isArray(rowData) || rowData.length === 0) {
    return [];
  }

  try {
    // Create regex for case-insensitive search
    const searchRegex = new RegExp(escapeRegExp(searchString.trim()), "i");

    // Filter rows where at least one field matches the search
    return rowData.filter((row) => {
      if (!row || typeof row !== "object") return false;

      return Object.keys(row).some((field) => {
        return doesFieldMatchSearch(row[field], searchRegex);
      });
    });
  } catch (error) {
    // Fallback: return original data if regex creation fails
    console.warn("Search regex creation failed:", error);
    return rowData;
  }
};

function GridTable({
  rowData,
  columnData,
  onClickAction,
  actions = [],
  onClickAdd,
  onClickFilter,
  filterButtonTitle = "Filters",
  topActionButtonTitle = "Add",
  toolTipName,
  isFilterAdded,
  hideSearchBar,
  hideAddButton,
  isFilterActive = false,
  onCsvUpload,
  showExportButton = false,
  onExport,
  filterButtonRef, // New prop to receive filter button ref
  statusFilter, // New prop for status filter dropdown component
  templateName
}) {
  const [searchString, setSearchString] = useState("");
  
  // Get current user role from localStorage
  const getUserRole = () => {
    try {
      const roleId = localStorage.getItem("roleId");
      return roleId || null;
    } catch (error) {
      return null;
    }
  };
  
  // Use ref to store onClickAction to prevent infinite loops
  const onClickActionRef = useRef(onClickAction);
  useEffect(() => {
    onClickActionRef.current = onClickAction;
  }, [onClickAction]);

  // Memoize filtered rows
  const rows = useMemo(() => {
    if (rowData && Array.isArray(rowData)) {
      return filterRowsBySearch(rowData, searchString);
    }
    return [];
  }, [rowData, searchString]);

  // Memoize processed columns
  const cols = useMemo(() => {
    if (!columnData || !Array.isArray(columnData)) {
      return [];
    }

    // Process columns to make them flexible and responsive
    const processedColumns = columnData.map((col) => {
      // If column has a fixed width but no flex, make it flexible with minWidth
      if (col.width && !col.flex && col.field !== "action") {
        return {
          ...col,
          minWidth: col.width,
          flex: 1,
          maxWidth: undefined, // Allow columns to grow on large screens
          // Remove fixed width to allow flexibility
          width: undefined,
        };
      }
      // If column has no width or flex, make it flexible
      if (!col.width && !col.flex && col.field !== "action") {
        return {
          ...col,
          flex: 1,
          minWidth: 120,
          maxWidth: undefined, // Allow columns to grow on large screens
        };
      }
      return col;
    });

    if (actions && actions.length > 0) {
      const actionColumn = {
        field: "action",
        headerName: "Action",
        width: 150,
        minWidth: 120,
        maxWidth: 150,
        flex: 0,
        headerClassName: "super-app-theme--header",
        headerAlign: "left",
        align: "left",
        renderCell: (param) => (
          <ActionOption
            row={param.row}
            actions={actions}
            onClick={(type) => {
              onClickActionRef.current?.(type, param.row, param.row.id);
            }}
          />
        ),
      };
      return [actionColumn, ...processedColumns];
    }
    
    return processedColumns;
  }, [columnData, actions]);

  const requestSearch = (searchValue) => {
    setSearchString(searchValue);
  };

  const handleFilterClick = () => {
    onClickFilter?.();
  };

  return (
    <div className="bg-white overflow-hidden w-full h-full flex flex-col shadow-xl rounded-lg relative">
      {/* Modern Enhanced Header Section - Sticky */}
      <div className="sticky top-0 z-50 relative bg-gradient-to-br from-[#0b3a5c] via-[#135e96] to-[#0b3a5c] p-5 sm:p-6 md:p-7 flex-shrink-0 shadow-2xl">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
        
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}></div>
        </div>
        
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full animate-shine"></div>
        
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
          {/* Search Bar Section - Modern Design */}
          {!hideSearchBar && (
            <div className="relative flex-1 w-full lg:max-w-lg order-1 lg:order-1">
              <div className="relative group">
                <div className="absolute inset-0 bg-white/30 rounded-2xl blur-lg group-hover:blur-xl transition-all duration-500 opacity-0 group-hover:opacity-100"></div>
                <div className="relative">
                  <input
                    className="w-full bg-white/95 backdrop-blur-md rounded-2xl px-5 sm:px-6 py-3.5 sm:py-4 pl-14 sm:pl-16 pr-14 sm:pr-16 text-sm sm:text-base font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-white/50 focus:bg-white shadow-2xl placeholder-gray-400 transition-all duration-300 border-2 border-white/30 hover:border-white/50 focus:border-white"
                    placeholder="Search records..."
                    value={searchString}
                    onChange={(e) => requestSearch(e.target.value)}
                  />
                  <div className="absolute left-5 sm:left-6 top-1/2 transform -translate-y-1/2">
                    <IoSearchSharp className="h-5 sm:h-6 w-5 sm:w-6 text-[#135e96] transition-all duration-300 group-hover:scale-110" />
                  </div>
                  {searchString && (
                    <button
                      className="absolute right-14 sm:right-16 top-1/2 transform -translate-y-1/2 p-2 hover:bg-red-50 rounded-xl transition-all duration-200 group"
                      onClick={() => requestSearch("")}
                    >
                      <RxCross2 className="w-4 h-4 text-gray-500 hover:text-red-600 group-hover:scale-125 transition-transform" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status Filter Section - If provided */}
          {statusFilter && (
            <div className="relative order-2 lg:order-2 lg:flex-shrink-0">
              {statusFilter}
            </div>
          )}

          {/* Action Buttons Section - Enhanced Design */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3 order-3 lg:order-3 lg:flex-shrink-0">
            {onClickFilter && (
              <Tooltip title="Apply Filters" arrow>
                <button
                  ref={filterButtonRef}
                  className={`relative px-5 sm:px-6 py-3 sm:py-3.5 flex items-center justify-center gap-2.5 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap overflow-hidden group ${
                    isFilterActive
                      ? "bg-gradient-to-r from-[#0b3a5c] to-[#135e96] text-white border-2 border-white/30"
                      : "bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-md text-white border-2 border-white/40 hover:from-white/30 hover:to-white/20 hover:border-white/60"
                  }`}
                  onClick={handleFilterClick}
                >
                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  
                  <span className="text-sm sm:text-base font-semibold relative z-10">
                    {filterButtonTitle}
                  </span>
                  <HiAdjustmentsHorizontal className="h-4 w-4 sm:h-5 sm:w-5 relative z-10 group-hover:rotate-180 transition-transform duration-300" />
                  
                  {/* Active indicator */}
                  {isFilterActive && (
                    <div className="absolute top-1 right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                  )}
                  
                  {/* Ripple effect */}
                  <span className="absolute inset-0 rounded-lg opacity-0 group-active:opacity-20 group-active:bg-white transition-opacity duration-150"></span>
                </button>
              </Tooltip>
            )}
            {/* ✅ Use CsvHandler Component (shown only when onCsvUpload provided) */}
            {Boolean(onCsvUpload) && (
              <CsvHandler
                rowData={rowData}
                onCsvUpload={onCsvUpload}
                downloadFileName="users-data.csv"
                templateName={templateName}
              />
            )}

            {/* Export Button (shown only when showExportButton is true) */}
            {showExportButton && (
              <Tooltip title="Export to Excel" arrow>
                <button
                  className="relative px-5 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white flex items-center justify-center gap-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap overflow-hidden group"
                  onClick={onExport}
                >
                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  
                  <FaFileExport className="h-4 w-4 sm:h-5 sm:w-5 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-sm sm:text-base font-semibold relative z-10">Export</span>
                  
                  {/* Ripple effect */}
                  <span className="absolute inset-0 rounded-lg opacity-0 group-active:opacity-30 group-active:bg-white transition-opacity duration-150"></span>
                </button>
              </Tooltip>
            )}

            {onClickAdd && !hideAddButton && (
              <Tooltip title={toolTipName} arrow>
                <button
                  className="relative px-5 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] text-white flex items-center justify-center gap-2.5 rounded-lg font-semibold hover:from-[#135e96] hover:to-[#0b3a5c] shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap overflow-hidden group"
                  onClick={() => {
                    onClickAdd?.();
                  }}
                >
                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  
                  {/* Icon with animation */}
                  <FaPlus className="h-4 w-4 sm:h-5 sm:w-5 relative z-10 group-hover:rotate-90 transition-transform duration-300" />
                  
                  {/* Text */}
                  <span className="text-sm sm:text-base font-semibold relative z-10">
                    {topActionButtonTitle}
                  </span>
                  
                  {/* Ripple effect */}
                  <span className="absolute inset-0 rounded-lg opacity-0 group-active:opacity-30 group-active:bg-white transition-opacity duration-150"></span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {/* Enhanced Data Grid - Full width and dynamic height */}
      <div className="w-full flex-1 min-h-0 bg-white mx-0 px-0 overflow-auto">
        <DataGrid
          sx={{
            border: "none",
            backgroundColor: "transparent",
            width: "100%",
            height: "100%",
            margin: 0,
            padding: 0,
            "& .MuiDataGrid-main": {
              width: "100%",
              overflow: "auto",
            },
            "& .MuiDataGrid-container--top": {
              width: "100%",
            },
            "& .MuiDataGrid-container--bottom": {
              width: "100%",
            },
            "& .MuiDataGrid-root": {
              width: "100%",
            },
            "& .MuiDataGrid-virtualScroller": {
              width: "100%",
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              width: "100% !important",
            },
            "& .MuiDataGrid-virtualScrollerRenderZone": {
              width: "100% !important",
            },
            "& .MuiDataGrid-scrollbar": {
              "& .MuiDataGrid-scrollbar--horizontal": {
                "& .MuiScrollbar-root": {
                  "& .MuiScrollbar-thumb": {
                    backgroundColor: "#94a3b8",
                    borderRadius: "6px",
                    border: "2px solid #f1f5f9",
                    "&:hover": {
                      backgroundColor: "#64748b",
                    },
                  },
                  "& .MuiScrollbar-track": {
                    backgroundColor: "#f1f5f9",
                    borderRadius: "6px",
                  },
                },
              },
              "& .MuiDataGrid-scrollbar--vertical": {
                "& .MuiScrollbar-root": {
                  "& .MuiScrollbar-thumb": {
                    backgroundColor: "#94a3b8",
                    borderRadius: "6px",
                    border: "2px solid #f1f5f9",
                    "&:hover": {
                      backgroundColor: "#64748b",
                    },
                  },
                  "& .MuiScrollbar-track": {
                    backgroundColor: "#f1f5f9",
                    borderRadius: "6px",
                  },
                },
              },
            },
            "& .super-app-theme--header": {
              fontSize: "12px",
              fontWeight: "700",
              color: "#1e293b",
              background: "#ffffff",
              borderBottom: "2px solid #e2e8f0",
              padding: "12px 20px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              boxShadow: "none",
              position: "relative",
            },
            "& .MuiDataGrid-cell": {
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              fontWeight: "400",
              paddingTop: "12px",
              paddingBottom: "12px",
              paddingLeft: "20px",
              paddingRight: "20px",
              borderBottom: "1px solid #f1f5f9",
              color: "#334155",
              transition: "all 0.2s ease-in-out",
              borderRight: "none",
              wordBreak: "break-word",
              overflow: "visible",
              whiteSpace: "normal",
              lineHeight: "1.6",
              display: "flex",
              alignItems: "center",
              "&:first-of-type": {
                paddingLeft: "20px",
              },
            },
            "& .MuiDataGrid-row": {
              transition: "all 0.2s ease-in-out",
              border: "none",
              margin: "0",
              backgroundColor: "#ffffff",
              borderRadius: "0",
              borderBottom: "1px solid #f1f5f9",
              boxShadow: "none",
              minHeight: "48px !important",
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                paddingTop: "12px",
                paddingBottom: "12px",
              },
              "&:hover": {
                backgroundColor: "#f8fafc",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e2e8f0",
                  color: "#1e293b",
                  fontWeight: "500",
                },
              },
              "&:nth-of-type(even)": {
                backgroundColor: "#fafbfc",
              },
              "&:nth-of-type(odd)": {
                backgroundColor: "#ffffff",
              },
              "&:last-of-type": {
                borderBottom: "none",
              },
              "&.Mui-selected": {
                backgroundColor: "#eff6ff",
                "&:hover": {
                  backgroundColor: "#dbeafe",
                },
              },
              "&.approved-status-row": {
                backgroundColor: "#eff6ff !important",
                borderLeft: "4px solid #3b82f6",
                "& .MuiDataGrid-cell": {
                  backgroundColor: "#eff6ff",
                  fontWeight: "500",
                  color: "#1e40af",
                },
                "&:hover": {
                  backgroundColor: "#dbeafe !important",
                  "& .MuiDataGrid-cell": {
                    backgroundColor: "#dbeafe",
                    borderBottom: "1px solid #93c5fd",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#eff6ff !important",
                },
                "&:nth-of-type(odd)": {
                  backgroundColor: "#eff6ff !important",
                },
              },
              "&.user-highlight-row": {
                backgroundColor: "#fef3c7 !important",
                borderLeft: "4px solid #f59e0b",
                "& .MuiDataGrid-cell": {
                  backgroundColor: "#fef3c7",
                  fontWeight: "500",
                  color: "#92400e",
                },
                "&:hover": {
                  backgroundColor: "#fde68a !important",
                  "& .MuiDataGrid-cell": {
                    backgroundColor: "#fde68a",
                    borderBottom: "1px solid #fbbf24",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#fef3c7 !important",
                },
                "&:nth-of-type(odd)": {
                  backgroundColor: "#fef3c7 !important",
                },
              },
              "&.manager-highlight-row": {
                backgroundColor: "#d1fae5 !important",
                borderLeft: "4px solid #10b981",
                "& .MuiDataGrid-cell": {
                  backgroundColor: "#d1fae5",
                  fontWeight: "500",
                  color: "#065f46",
                },
                "&:hover": {
                  backgroundColor: "#a7f3d0 !important",
                  "& .MuiDataGrid-cell": {
                    backgroundColor: "#a7f3d0",
                    borderBottom: "1px solid #34d399",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#d1fae5 !important",
                },
                "&:nth-of-type(odd)": {
                  backgroundColor: "#d1fae5 !important",
                },
              },
            },
            "& .MuiDataGrid-columnHeaders": {
              borderRadius: "0",
              borderBottom: "2px solid #e2e8f0",
              background: "#ffffff",
              boxShadow: "none",
              margin: "0",
              position: "sticky",
              top: 0,
              zIndex: 40,
              "& .MuiDataGrid-columnHeader": {
                borderRight: "none",
                "&:last-child": {
                  borderRight: "none",
                },
                "&:hover": {
                  backgroundColor: "#f8fafc",
                  "& .super-app-theme--header": {
                    color: "#135e96",
                  },
                },
                "&:first-of-type": {
                  paddingLeft: "20px",
                },
              },
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "#f8fafc",
              borderTop: "2px solid #e2e8f0",
              padding: "10px 20px",
              minHeight: "48px",
              overflow: "visible",
              borderRadius: "0",
              boxShadow: "none",
              background: "#f8fafc",
              margin: "0",
            },
            "& .MuiTablePagination-root": {
              color: "#1e293b",
              fontWeight: "600",
              fontSize: "14px",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontWeight: "600",
                color: "#0f172a",
                fontSize: "14px",
              },
            "& .MuiTablePagination-select": {
              fontWeight: "600",
              color: "#135e96",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              padding: "4px 8px",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            },
            "& .MuiTablePagination-actions": {
              "& button": {
                color: "#135e96",
                "&:hover": {
                  backgroundColor: "#e0f2fe",
                },
                "&.Mui-disabled": {
                  color: "#cbd5e1",
                },
              },
            },
            "& .MuiDataGrid-selectedRowCount": {
              color: "#1e293b",
              fontWeight: "600",
            },
            "& .MuiDataGrid-main": {
              overflow: "auto",
              padding: "0",
              margin: "0",
              width: "100%",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f5f9",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#94a3b8",
                },
              },
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto !important",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f5f9",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#94a3b8",
                },
              },
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              overflow: "visible",
              width: "100% !important",
            },
            "& .MuiDataGrid-root": {
              width: "100%",
              margin: "0",
              padding: "0",
              border: "none",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
              borderBottom: "none",
            },
          }}
          getRowHeight={(params) => "auto"}
          getRowClassName={(params) => {
            const userRole = getUserRole();
            const status = params.row.currentAppraisalStatus;
            
            // Admin: Highlight APPROVED (Pending Final Review)
            if (userRole === ROLES.admin && status === "APPROVED") {
              return "approved-status-row";
            }
            
            // User: Highlight NOT_STARTED and CHANGE_REQUEST
            if (userRole === ROLES.emp && (status === "NOT_STARTED" || status === "CHANGE_REQUEST")) {
              return "user-highlight-row";
            }
            
            // Manager: Highlight COMPLETED (Manager Review)
            if (userRole === ROLES.mgr && status === "COMPLETED") {
              return "manager-highlight-row";
            }
            
            return "";
          }}
          className="bg-transparent"
          rows={rows || []}
          columns={cols || []}
          rowSelection={false}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnMenu={true}
          disableRowSelectionOnClick={true}
          hideFooterSelectedRowCount={false}
          autoHeight={false}
          disableColumnResize={false}
          columnHeaderHeight={44}
          // getRowHeight={() => null}
          getEstimatedRowHeight={() => 48}
        />
      </div>
    </div>
  );
}

// PropTypes validation
GridTable.propTypes = {
  rowData: PropTypes.array,
  columnData: PropTypes.array,
  onClickAction: PropTypes.func,
  actions: PropTypes.array,
  onClickAdd: PropTypes.func,
  onClickFilter: PropTypes.func,
  filterButtonTitle: PropTypes.string,
  topActionButtonTitle: PropTypes.string,
  toolTipName: PropTypes.string,
  isFilterAdded: PropTypes.bool,
  hideSearchBar: PropTypes.bool,
  hideAddButton: PropTypes.bool,
  isFilterActive: PropTypes.bool,
  onCsvUpload: PropTypes.func,
  showExportButton: PropTypes.bool,
  onExport: PropTypes.func,
  filterButtonRef: PropTypes.object,
  statusFilter: PropTypes.node,
};

export default GridTable;

const ActionOption = ({ onClick, actions = [], row }) => {
  return (
    <div className="flex items-center justify-center gap-2 h-full flex-wrap">
      {actions.includes("DELETE") && (
        <Tooltip title="Delete" arrow>
          <button
            className="p-2.5 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg border border-transparent hover:border-red-200 group flex items-center justify-center flex-shrink-0"
            onClick={() => onClick?.("DELETE")}
          >
            <TbTrash className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </Tooltip>
      )}

      {actions.includes("EDIT") && (
        <Tooltip title="Edit" arrow>
          <button
            className="p-2.5 text-blue-600 hover:bg-blue-50 hover:text-blue-700 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg border border-transparent hover:border-blue-200 group flex items-center justify-center flex-shrink-0"
            onClick={() => onClick?.("EDIT")}
          >
            <FiEdit className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </Tooltip>
      )}

      {actions.includes("VIEW") && (
        <Tooltip title="View" arrow>
          <button
            className="p-2.5 text-gray-600 hover:bg-gray-50 hover:text-gray-700 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg border border-transparent hover:border-gray-200 group flex items-center justify-center"
            onClick={() => onClick?.("VIEW")}
          >
            <FiEye className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </Tooltip>
      )}

      {actions.includes("REVIEW") && (
        <Tooltip title="Review" arrow>
          <button
            className="p-2.5 text-purple-600 hover:bg-purple-50 hover:text-purple-700 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg border border-transparent hover:border-purple-200 group flex items-center justify-center flex-shrink-0"
            onClick={() => onClick?.("REVIEW")}
          >
            <MdRateReview className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>
        </Tooltip>
      )}

      {actions.includes("APPRAISAL_REVIEW") && (
        <>
          {row.currentAppraisalStatus === "APPROVED" ? (
            <Tooltip title="Review Appraisal" arrow>
              <button
                className="p-2.5 text-blue-600 hover:bg-blue-50 hover:text-blue-700 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg border border-transparent hover:border-blue-200 group flex items-center justify-center"
                onClick={() => onClick?.("Review")}
              >
                <FiEye className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            </Tooltip>
          ) : (
            row.currentAppraisalStatus === "FINAL_REVIEWED" && (
              <Tooltip title="View Appraisal" arrow>
                <button
                  className="p-2.5 text-green-600 hover:bg-green-50 hover:text-green-700 rounded-xl transition-all duration-300 hover:scale-110 hover:shadow-lg border border-transparent hover:border-green-200 group flex items-center justify-center"
                  onClick={() => onClick?.("View")}
                >
                  <FiEye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </button>
              </Tooltip>
            )
          )}
        </>
      )}
    </div>
  );
};

// PropTypes for ActionOption component
ActionOption.propTypes = {
  onClick: PropTypes.func,
  actions: PropTypes.array,
  row: PropTypes.object,
};
